import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../theme';
import { CalendarCard, CalendarCardProps } from './CalendarCard';
import { useLocale } from '../../contexts/LocaleContext';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface CalendarListProps {
  calendars: Array<CalendarCardProps['calendar']>;
  loading?: boolean;
  refreshing?: boolean;
  onRefresh?: () => void;
  onCalendarPress?: (calendarId: string) => void;
  onCalendarEdit?: (calendarId: string) => void;
  onCalendarDelete?: (calendarId: string) => void;
  showActions?: boolean;
  emptyMessage?: string;
  containerStyle?: any;
}

export const CalendarList: React.FC<CalendarListProps> = ({
  calendars,
  loading = false,
  refreshing = false,
  onRefresh,
  onCalendarPress,
  onCalendarEdit,
  onCalendarDelete,
  showActions = true,
  emptyMessage,
  containerStyle,
}) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const resolvedEmptyMessage = emptyMessage ?? t('calendar.calendarList.emptyDefault');
  const renderCalendar = ({ item }: { item: CalendarCardProps['calendar'] }) => {
    return (
      <CalendarCard
        calendar={item}
        onPress={onCalendarPress}
        onEdit={onCalendarEdit}
        onDelete={onCalendarDelete}
        showActions={showActions}
      />
    );
  };

  const renderEmpty = () => {
    if (loading) {
      return (
        <View style={styles.emptyContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.emptyText}>{t('calendar.calendarList.loading')}</Text>
        </View>
      );
    }

    return (
      <View style={styles.emptyContainer}>
        <Ionicons name="calendar-outline" size={64} color={colors.textLight} />
        <Text style={styles.emptyText}>{resolvedEmptyMessage}</Text>
      </View>
    );
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <FlatList
        data={calendars}
        renderItem={renderCalendar}
        keyExtractor={(item, index) => item._id || `calendar-${index}`}
        ListEmptyComponent={renderEmpty}
        refreshControl={
          onRefresh ? (
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[colors.primary]}
              tintColor={colors.primary}
            />
          ) : undefined
        }
        contentContainerStyle={
          calendars.length === 0 ? styles.emptyListContent : styles.listContent
        }
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
  },
  listContent: {
    padding: spacing.md,
  },
  emptyListContent: {
    flexGrow: 1,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  emptyText: {
    ...typography.h3,
    color: colors.textLight,
    marginTop: spacing.md,
    textAlign: 'center',
  },
  emptySubtext: {
    ...typography.body,
    color: colors.textLight,
    marginTop: spacing.xs,
    textAlign: 'center',
  },
});

